import React, { FC, useEffect, useMemo } from "react";
import {
	Field,
	FieldRenderProps,
	FieldWrapper,
} from "@progress/kendo-react-form";
import {
	FormDropDownList,
	FormDatePicker,
	FormInput,
	FormDropDownListWithSearch,
	ItemLabel,
} from "ui/Topography";
import { useState } from "react";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import {
	getCatchAreaApi,
	getErsApi,
	getFishingGearApi,
	getQuotaTypeApi,
	getTerminalsApi,
} from "api/common";
import {
	getCountriesApi,
	getFishingDepartmentIdNumbersApi,
	getFoodSafetyApprovalNumberApi,
	getSearchBusinessNumberApi,
	getSearchUsageApi,
} from "api/reportSearch";
import { SalesNoteHead } from "interface/salesNote";
import {
	DROPDOWN_ITEMS,
	getLandingTypeField,
	getYesOrNoField,
} from "utils/dropdown/items";
import { DATE_FORMAT } from "utils/constant";
import { t } from "i18next";
import {
	CatchArea,
	Country,
	DropDownItem,
	ErsMessage,
	FishingGear,
} from "interface/common";
import { saveSalesNoteHead } from "api/salesNote";
import { useInfoStore } from "store/error";
import { getExemptionVesselApi } from "api/vessel";
import { ExemptionVessel } from "interface/vessel";
import { QuotaVesselRenderItem } from "ui/dropdownItemList/VesselRenderItem";
import {
	areAllRequirementsMet,
	requirementFields,
	handleRequirementCheck,
} from "../utils";
import { useToastStore } from "store/toast";


const formatDate = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	// return `${day}.${month}.${year}`;
	return `${year}-${month}-${day}`;
};

const FormStepOne: FC<{
	form: SalesNoteHead;
	setForm: (form: SalesNoteHead) => void;
	setAllRequirementsMet: (allMet: boolean) => void;
	showRequirementMetError: boolean;
	setFormSaved: (saved: boolean) => void;
}> = ({
	form,
	setForm,
	setAllRequirementsMet,
	showRequirementMetError,
	setFormSaved,
}) => {
	const [buyersFd, setBuyersFd] = useState<DropDownItem[]>([]);
	const [quotaType, setQuotaType] = useState<DropDownItem[]>([]);
	const [quotaVessel, setQuotaVessel] = useState<ExemptionVessel[]>([]);
	const [countries, setCountries] = useState<Country[]>([]);
	const [business, setBusiness] = useState<DropDownItem[]>([]);
	const [foodSafetyApproveNumber, setFoodSafetyApproveNumber] = useState<
		DropDownItem[]
	>([]);

	const [erMessages, setErMessages] = useState<ErsMessage[]>([]);
	const [firstCatchDateTemp, setFirstCatchDateTemp] = useState<Date | null>(null);
	const [lastCatchDateTemp, setLastCatchDateTemp] = useState<Date | null>(null);
	const { setError } = useInfoStore();
	const { showWarning } = useToastStore();

	const landingNoteRefExist =
		form.referredNoteNumber !== undefined && form.referredNoteNumber !== null;
	const allowQuotaNationEditCode = "04"; // "ANNET LANDS KVOTE";

	const [requirementMet, setRequirementMet] = useState(requirementFields);

	useEffect(() => {
		let recordNumberErsPassed = true;
		if (landingNoteRefExist) {
			recordNumberErsPassed = true;
		} else {
			if (
				erMessages.length > 0 &&
				(!form.recordNumberErs || form.recordNumberErs === null)
			) {
				recordNumberErsPassed = false;
			}
		}
		setRequirementMet(
			handleRequirementCheck(form, recordNumberErsPassed, showVesselQuota),
		);
	}, [form, erMessages]);

	useEffect(() => {
		setAllRequirementsMet(areAllRequirementsMet(requirementMet));
	}, [requirementMet]);

	useEffect(() => {
		getErs();
		getQuotaVessel();
	}, [form]);

	useEffect(() => {
		if (!form.buyerLegalEntitySubUnitNo) return;
		fdNumber(form.buyerLegalEntitySubUnitNo);
	}, [form.buyerLegalEntitySubUnitNo]);

	useEffect(() => {
		getBusiness();
	}, [form.buyerLegalEntityMainUnitNo]);

	const getQuotaTypeData = () => {
		getSearchUsageApi()
			.then((data) => {})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
		getQuotaTypeApi()
			.then((data) => {
				setQuotaType(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const getCountries = () => {
		getCountriesApi()
			.then((data) => {
				setCountries(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const getBusiness = () => {
		if (!form.buyerLegalEntityMainUnitNo) {
			return;
		}

		getSearchBusinessNumberApi(form.buyerLegalEntityMainUnitNo.trim())
			.then((data) => {
				if (data.length === 1 && form.buyerLegalEntitySubUnitNo === undefined) {
					saveForm({
						...form,
						buyerLegalEntitySubUnitName: data[0].displayName,
						buyerLegalEntitySubUnitNo: data[0].code,
					});
				}

				setBusiness(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const getFoodSafetyApprovalNumber = () => {
		if (!form.buyerFdirId) {
			return;
		}
		getFoodSafetyApprovalNumberApi(form.buyerFdirId)
			.then((data) => {
				setFoodSafetyApproveNumber(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const fdNumber = (id?: string) => {
		getFishingDepartmentIdNumbersApi(
			id ? id : (form.buyerLegalEntitySubUnitNo as any),
		)
			.then((buyers) => {
				//console.log('id', id, form.buyerLegalEntitySubUnitNo);

				if (buyers.length === 1 && form.buyerFdirId === undefined) {
					saveForm({ ...form, buyerFdirId: buyers[0].code as any });
					// setForm({ ...form, buyerFdirId: buyers[0].code as any });
				}

				//console.log('setBuyersFd', buyers);

				setBuyersFd(buyers);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};
	// console.log("form in step one", form);
	const saveForm = (form: SalesNoteHead) => {
		setForm(form);
		form.noteStatus = 2;
		setFormSaved(false);
		saveSalesNoteHead(form)
			.then((response) => {
				setFormSaved(true);
			})
			.catch((err) => {
				console.log(err);
				// if (err?.response?.data) {
				// 	setError(err?.response?.data.returnText);
				// } else {
				// 	setError(err.message);
				// }
			});
	};

	const getQuotaVessel = () => {
		if (!form.vesselHullId || quotaVessel.length > 0) {
			return;
		}
		getExemptionVesselApi(form.vesselHullId, form.lastCatchDate as any)
			.then((data) => {
				setQuotaVessel(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const getErs = () => {
		if (erMessages.length > 0 || !form?.vesselRadioCallSignal) {
			return;
		}

		getErsApi({
			landingTime: form.landingTime || (new Date() as any),
			radioCallSign: form.vesselRadioCallSignal as any,
		})
			.then((data) => {
				setErMessages(data);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const ersData = erMessages.find(
		(msg) => msg.recordNumber === form.recordNumberErs,
	);
	const showVesselQuota = quotaVessel.length > 0;

	const ensureDate = (value: any): Date | null => {
		if (value === null || value === undefined) {
			return null;
		}
		return value instanceof Date ? value : new Date(value);
	};

	const getTempOrFormValue = (tempValue: Date | null, formValue: any) => {
		if (tempValue !== null) {
			return tempValue;
		}
		return ensureDate(formValue);
	};

	// Memoize the values to prevent infinite re-renders
	const firstCatchDateValue = useMemo(
		() => getTempOrFormValue(firstCatchDateTemp, form.firstCatchDate),
		[firstCatchDateTemp, form.firstCatchDate],
	);

	const lastCatchDateValue = useMemo(
		() => getTempOrFormValue(lastCatchDateTemp, form.lastCatchDate),
		[lastCatchDateTemp, form.lastCatchDate],
	);

	return (
		<>
			<div
				className="sales-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				{/*<Field*/}
				{/*	id={"Fangstdagbok"}*/}
				{/*	name={"Fangstdagbok"}*/}
				{/*	label={t("CatchDiary") + "*"}*/}
				{/*	disabled={landingNoteRefExist}*/}
				{/*	// placeholder={"example"}*/}
				{/*	currentValue={form?.diaryNumber}*/}
				{/*	type={"number"}*/}
				{/*	component={FormInput}*/}
				{/*	isValid={handleIsValid(*/}
				{/*		showRequirementMetError,*/}
				{/*		requirementMet.diaryNumber,*/}
				{/*	)}*/}
				{/*	onChange={(e: any) => {*/}
				{/*		saveForm({ ...form, diaryNumber: e.value || null });*/}
				{/*	}}*/}
				{/*/>*/}

				<Field
					id={"Tur_nummer*"}
					name={"Tur nummer*"}
					label={t("TripNumber") + "*"}
					// placeholder={"emt"}
					disabled={landingNoteRefExist}
					component={FormInput}
					currentValue={form?.tripNumber}
					type={"number"}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.tripNumber,
					)}
					onChange={(e: any) => {
						saveForm({ ...form, tripNumber: e.value || null });
					}}
				/>

				<Field
					id={"ERS-meldingsnr"}
					name={"ERS-meldingsnr"}
					label={t("ErsMessageNumber") + "*"}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.recordNumberErs,
						)
					}
					textField={"displayName"}
					dataItemKey={"recordNumber"}
					disabled={landingNoteRefExist}
					data={erMessages}
					currentValue={{
						recordNumber: ersData?.recordNumber,
						displayName: ersData?.displayName,
					}}
					loadDataOnClicked={getErs}
					onSelect={(data: ErsMessage) => {
						let _data: any = {
							recordNumberErs: data.recordNumber || undefined,
							ersId: data.ersId,
						};
						if (!form.vesselCaptainsName) {
							_data = {
								..._data,
								vesselCaptainsName: data.vesselMaster || undefined,
							};
						}
						saveForm({
							...form,
							..._data,
						});
					}}
					component={FormDropDownList}
					//	onChange={(e: any) => setForm({ ...form, diaryNumber: e.value })}
				/>
				<Field
					id={"FørsteFangstdato*"}
					name={"FørsteFangstdato"}
					label={t("FirstCatchDate") + "*"}
					currentValue={firstCatchDateValue}
					disabled={landingNoteRefExist}
					placeholder=""
					format={DATE_FORMAT}
					max={lastCatchDateValue ? new Date(lastCatchDateValue) : undefined}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.firstCatchDate,
					)}
					onChange={(e: any) => {
						setFirstCatchDateTemp(e.value);
					}}
					saveItem={() => {
						if (!firstCatchDateTemp) {
							setFirstCatchDateTemp(null);
							return;
						}
						if (
							form.lastCatchDate &&
							new Date(firstCatchDateTemp).setHours(0, 0, 0, 0) >
								new Date(form.lastCatchDate).setHours(0, 0, 0, 0)
						) {
							showWarning(t("FirstCatchDateLessThanLastCatchDate"), {
								duration: 6000,
								pauseOnHover: true,
								position: "bottom-center",
								style: {
									paddingTop: "40px",
									paddingBottom: "40px",
								},
							});
							setFirstCatchDateTemp(null);
							return;
						}
						saveForm({ ...form, firstCatchDate: formatDate(firstCatchDateTemp) });
						setFirstCatchDateTemp(null);
					}}
					component={FormDatePicker}
				/>

				<Field
					id={"Siste_Fangstdato*"}
					name={"Siste_Fangstdato*"}
					label={t("LastCatchDate") + "*"}
					disabled={landingNoteRefExist}
					//currentValue={lastCatchDateTemp !== null ? lastCatchDateTemp : form.lastCatchDate}
					currentValue={lastCatchDateValue}
					min={firstCatchDateValue ? new Date(firstCatchDateValue) : undefined}
					onChange={(e: any) => {
						setLastCatchDateTemp(e.value);
					}}
					saveItem={() => {
						if (!lastCatchDateTemp) {
							setLastCatchDateTemp(null);
							return;
						}
						if (
							form.firstCatchDate &&
							new Date(lastCatchDateTemp).setHours(0, 0, 0, 0) <
								new Date(form.firstCatchDate).setHours(0, 0, 0, 0)
						) {
							showWarning(t("LastCatchDateGreaterThanFirstCatchDate"), {
								duration: 6000,
								pauseOnHover: true,
								position: "bottom-center",
								style: {
									paddingTop: "40px",
									paddingBottom: "40px",
								},
							});
							setLastCatchDateTemp(null);
							return;
						}
						saveForm({
							...form,
							lastCatchDate: formatDate(lastCatchDateTemp),
							catchYear: lastCatchDateTemp.getFullYear(),
						});
						//setLastCatchDateTemp(null);
					}}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.lastCatchDate,
					)}
					placeholder=""
					format={DATE_FORMAT}
					component={FormDatePicker}
				/>

				<Field
					id={"Fangst"}
					name={"Fangstår"}
					label={t("CatchYear") + "*"}
					disabled={landingNoteRefExist}
					placeholder={""}
					currentValue={form.catchYear}
					component={FormInput}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.catchYear,
					)}
					type={"number"}
					onChange={(e: any) => {
						saveForm({ ...form, catchYear: e.value });
					}}
				/>
			</div>

			<div
				className="sales-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				<Field
					id={"Mannskap"}
					name={"Mannskap"}
					label={t("CrewSize") + "*"}
					disabled={landingNoteRefExist}
					placeholder={""}
					component={FormInput}
					currentValue={form.crewSize}
					type={"number"}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.crewSize,
					)}
					onChange={(e: any) =>
						saveForm({ ...form, crewSize: e.value || null })
					}
				/>
				<Field
					id={"Herav_norsk_mannskap*"}
					name={"Herav_norsk_mannskap*"}
					label={t("CrewSizeNorwegian") + "*"}
					placeholder={""}
					component={FormInput}
					disabled={landingNoteRefExist}
					currentValue={form.norwegianCrewSize}
					type={"number"}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.norwegianCrewSize,
					)}
					onChange={(e: any) =>
						saveForm({ ...form, norwegianCrewSize: e.value || null })
					}
				/>

				<Field
					id={"Norsk_skipper*"}
					name={"Norsk_skipper*"}
					label={t("VesselOfficersNorwegian") + "*"}
					disabled={landingNoteRefExist}
					data={DROPDOWN_ITEMS.yesOrNo}
					textField={"field"}
					dataItemKey={"value"}
					// defaultItem={{ field: "Choose", value: "" }}
					currentValue={{
						value: form?.vesselOfficersNorwegian,
						field: getYesOrNoField(form?.vesselOfficersNorwegian as any).field,
					}}
					onSelect={(data: { field: string; value: number | string }) => {
						let value: number | undefined | string = data.value;
						if (data.value === "") {
							value = undefined;
						}

						saveForm({
							...form,
							vesselOfficersNorwegian: value as any,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.vesselOfficersNorwegian,
						)
					}
					component={FormDropDownList}
				/>
				<Field
					id={"Skipper"}
					name={"Skipper"}
					label={t("Skipper") + "*"}
					placeholder={""}
					component={FormInput}
					disabled={landingNoteRefExist}
					currentValue={form.vesselCaptainsName}
					isValid={handleIsValid(
						showRequirementMetError,
						requirementMet.vesselCaptainsName,
					)}
					onChange={(e: any) => {
						setForm({ ...form, vesselCaptainsName: e.value });
						//saveForm({ ...form, vesselCaptainsName: e.value })
					}}
					saveItem={(e: any) => {
						saveForm(form);
					}}
				/>
				{/*<Field*/}
				{/*	id={"Avgangsdatodato"}*/}
				{/*	name={"Avgangsdatodato"}*/}
				{/*	label={t("DepartureDate")}*/}
				{/*	currentValue={form.departureDate}*/}
				{/*	disabled={landingNoteRefExist}*/}
				{/*	placeholder={""}*/}
				{/*	format={DATE_FORMAT}*/}
				{/*	//min={form.firstCatchDate ? new Date(form.firstCatchDate) : undefined}*/}
				{/*	max={form.firstCatchDate ? new Date(form.firstCatchDate) : undefined}*/}
				{/*	onChange={(e) => {*/}
				{/*		if (*/}
				{/*			form.arrivalDate &&*/}
				{/*			new Date(e.value) > new Date(form.arrivalDate as any)*/}
				{/*		) {*/}
				{/*			showWarning(t("DepartureDateLessThanArrivalDate"), {*/}
				{/*				duration: 6000,*/}
				{/*				pauseOnHover: true,*/}
				{/*				position: "bottom-center",*/}
				{/*				style: {*/}
				{/*					paddingTop: "40px",*/}
				{/*					paddingBottom: "40px",*/}
				{/*				},*/}
				{/*			});*/}
				{/*			return;*/}
				{/*		}*/}
				{/*		saveForm({ ...form, departureDate: formatDate(e.value) });*/}
				{/*	}}*/}
				{/*	component={FormDatePicker}*/}
				{/*/>*/}

				{/*<Field*/}
				{/*	id={"Ankomstdato"}*/}
				{/*	name={"Ankomstdato"}*/}
				{/*	label={t("ArrivalDate")}*/}
				{/*	disabled={landingNoteRefExist}*/}
				{/*	currentValue={form.arrivalDate}*/}
				{/*	value={form.arrivalDate}*/}
				{/*	placeholder={""}*/}
				{/*	format={DATE_FORMAT}*/}
				{/*	min={form.lastCatchDate ? new Date(form.lastCatchDate) : undefined}*/}
				{/*	onChange={(e) => {*/}
				{/*		if (*/}
				{/*			form.departureDate &&*/}
				{/*			new Date(e.value) < new Date(form.departureDate as any)*/}
				{/*		) {*/}
				{/*			showWarning(t("ArrivalDateGreaterThanDepartureDate"), {*/}
				{/*				duration: 6000,*/}
				{/*				pauseOnHover: true,*/}
				{/*				position: "bottom-center",*/}
				{/*				style: {*/}
				{/*					paddingTop: "40px",*/}
				{/*					paddingBottom: "40px",*/}
				{/*				},*/}
				{/*			});*/}
				{/*			return;*/}
				{/*		}*/}
				{/*		saveForm({ ...form, arrivalDate: formatDate(e.value) });*/}
				{/*	}}*/}
				{/*	component={FormDatePicker}*/}
				{/*/>*/}
			{/*	<Field*/}
			{/*		id={"Fangstdøgn"}*/}
			{/*		name={"Fangstdøgn"}*/}
			{/*		label={t("DaysFished")}*/}
			{/*		placeholder={""}*/}
			{/*		disabled={landingNoteRefExist}*/}
			{/*		currentValue={form.daysFished}*/}
			{/*		component={FormInput}*/}
			{/*		type={"number"}*/}
			{/*		onChange={(e) => {*/}
			{/*			saveForm({ ...form, daysFished: e.value || null });*/}
			{/*		}}*/}
			{/*	/>*/}
			</div>

			<div
				className="sales-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				<Field
					id={"Redskap*"}
					name={"Redskap"}
					label={t("FishingGear") + "*"}
					value={t("ChooseFishingGear")}
					filterable={true}
					textField={"displayName"}
					dataItemKey={"id"}
					disabled={landingNoteRefExist}
					filterApi={getFishingGearApi}
					defaultItem={{
						displayName: t("ChooseFishingGear"),
						id: null,
					}}
					currentValue={{
						id: form.fishingGearId,
						displayName: form.fishingGear,
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.fishingGearId,
						)
					}
					// currentValue={{
					// 	id: form.fishingGearId,
					// 	displayName: form?.fishingGear,
					// }}
					onSelect={(fishingGear: FishingGear) => {
						saveForm({
							...form,
							fishingGearId: fishingGear.id,
							fishingGear: fishingGear.name,
						});
					}}
					component={FormDropDownListWithSearch}
				/>

				<Field
					name={"Fangstfelt*"}
					//label={"Fangstfelt"}
					label={t("CatchArea") + "*"}
					filterable={true}
					textField={"displayName"}
					dataItemKey={"id"}
					disabled={landingNoteRefExist}
					filterApi={getCatchAreaApi}
					defaultItem={{
						displayName: t("ChooseCatchArea"),
						id: null,
					}}
					currentValue={{
						id: form.catchField,
						displayName: form.catchField
							? `${form.catchField} | ${form.catchAreaZone} | ${form.catchAreaQuotaZone} | ${form.catchArea}`
							: "",
					}}
					showErrorMessage={
						!handleIsValid(showRequirementMetError, requirementMet.catchField)
					}
					onSelect={(catchArea: CatchArea) => {
						saveForm({
							...form,
							catchField: catchArea.code,
							catchAreaZone: catchArea.zone,
							catchAreaQuotaZone: catchArea.groundFDZone,
							catchArea: catchArea.name,
						});
					}}
					component={FormDropDownListWithSearch}
				/>
			</div>

			<div
				className="sales-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				<Field
					id={"Kvotetype*"}
					name={"Kvotetype*"}
					label={t("QuotaType") + "*"}
					textField={"name"}
					dataItemKey={"code"}
					disabled={landingNoteRefExist}
					data={quotaType}
					currentValue={{ code: form.quotaTypeCode, name: form.quotaType }}
					loadDataOnClicked={getQuotaTypeData}
					onChange={(e: { value: { code: string; name: string } }) => {
						const data: any = {
							...form,
							quotaTypeCode: e.value.code,
							quotaType: e.value.name,
						};
						if (e.value.code !== allowQuotaNationEditCode) {
							data.quotaNationCode = "NOR";
							data.quotaNation = "NORGE";
						}
						saveForm({
							...data,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.quotaTypeCode,
						)
					}
					component={FormDropDownList}
				/>

				<Field
					id={"Kvotenasjon"}
					name={"Kvotenasjon"}
					label={t("QuotaNation") + "*"}
					placeholder={""}
					textField={"englishDescription"}
					dataItemKey={"code"}
					loadDataOnClicked={getCountries}
					data={countries}
					currentValue={{
						code: form.quotaNationCode,
						englishDescription: form.quotaNation,
					}}
					disabled={
						landingNoteRefExist
							? true
							: form?.quotaTypeCode !== allowQuotaNationEditCode
					}
					onChange={(e: { value: Country }) => {
						saveForm({
							...form,
							quotaNationCode: e.value.code,
							quotaNation: e.value.description,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.quotaNationCode,
						)
					}
					component={FormDropDownList}
				/>

				<Field
					id={"Landingsnasjon"}
					name={"Landingsnasjon"}
					label={t("LandingNation") + "*"}
					textField={"englishDescription"}
					dataItemKey={"code"}
					loadDataOnClicked={getCountries}
					data={countries}
					disabled={landingNoteRefExist}
					currentValue={{
						code: form.landingNationCode,
						englishDescription: form.landingNation,
					}}
					onChange={(e: { value: Country }) => {
						saveForm({
							...form,
							landingNationCode: e.value.code,
							landingNation: e.value.description,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.landingNationCode,
						)
					}
					component={FormDropDownList}
				/>
				<Field
					id={"Landingstype"}
					name={"Landingstype"}
					label={t("LandingType") + "*"}
					data={DROPDOWN_ITEMS.landingType}
					textField={"field"}
					dataItemKey={"value"}
					currentValue={getLandingTypeField(form.partialLandingId as any)}
					disabled={landingNoteRefExist}
					component={FormDropDownList}
					onChange={(e: { value: { field: string; value: number } }) => {
						saveForm({ ...form, partialLandingId: e.value.value });
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.partialLandingId,
						)
					}
				/>
			</div>

			{/*<div
				className="sales-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: "1fr 1fr 1fr 1fr ",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>*/}
				{/*<Field*/}
				{/*	id={"Forrige sted"}*/}
				{/*	name={"Forrige sted"}*/}
				{/*	label={t("PreviousTerminal")}*/}
				{/*	filterable={true}*/}
				{/*	textField={"displayName"}*/}
				{/*	dataItemKey={"code"}*/}
				{/*	filterApi={getTerminalsApi}*/}
				{/*	disabled={landingNoteRefExist}*/}
				{/*	defaultItem={{*/}
				{/*		displayName: "Choose",*/}
				{/*		code: null,*/}
				{/*	}}*/}
				{/*	currentValue={{*/}
				{/*		code: form.previousReceivingFacility,*/}
				{/*		displayName: form.previousReceivingFacilityText,*/}
				{/*	}}*/}
				{/*	// className="error"*/}
				{/*	onSelect={(data: DropDownItem) => {*/}
				{/*		saveForm({*/}
				{/*			...form,*/}
				{/*			previousReceivingFacility: data.code,*/}
				{/*			previousReceivingFacilityText: data.displayName,*/}
				{/*		});*/}
				{/*	}}*/}
				{/*	component={FormDropDownListWithSearch}*/}
				{/*/>*/}
				{/*<Field*/}
				{/*	id={"Neste sted"}*/}
				{/*	name={"Neste sted"}*/}
				{/*	label={t("NextTerminal")}*/}
				{/*	filterable={true}*/}
				{/*	textField={"displayName"}*/}
				{/*	dataItemKey={"code"}*/}
				{/*	filterApi={getTerminalsApi}*/}
				{/*	disabled={landingNoteRefExist}*/}
				{/*	defaultItem={{*/}
				{/*		displayName: "Choose",*/}
				{/*		code: null,*/}
				{/*	}}*/}
				{/*	currentValue={{*/}
				{/*		code: form.nextReceivingFacility,*/}
				{/*		displayName: form.nextReceivingFacilityText,*/}
				{/*	}}*/}
				{/*	// className="error"*/}
				{/*	onSelect={(data: DropDownItem) => {*/}
				{/*		saveForm({*/}
				{/*			...form,*/}
				{/*			nextReceivingFacility: data.code,*/}
				{/*			nextReceivingFacilityText: data.displayName,*/}
				{/*		});*/}
				{/*	}}*/}
				{/*	component={FormDropDownListWithSearch}*/}
				{/*/>*/}
			{/*</div>*/}

			<div
				className="sales-note-form-grid"
				style={{
					display: "grid",
					gridTemplateColumns: showVesselQuota
						? "1fr 1fr 1fr 1fr 1fr"
						: "1fr 1fr 1fr 1fr",
					justifyContent: "space-between",
					gap: "20px",
					marginBottom: "25px",
				}}
			>
				<Field
					id={"Virksomhetsnummer"}
					name={"Virksomhetsnummer"}
					label={t("BusinessNumber") + "*"}
					data={business}
					textField={"displayName"}
					dataItemKey={"code"}
					currentValue={{
						code: form.buyerLegalEntitySubUnitNo?.trim(),
						displayName: form.buyerLegalEntitySubUnitName,
					}}
					loadDataOnClicked={getBusiness}
					onSelect={(data: DropDownItem) => {
						saveForm({
							...form,
							buyerLegalEntitySubUnitName: data.displayName,
							buyerLegalEntitySubUnitNo: data.code,
						});
					}}
					showErrorMessage={
						!handleIsValid(
							showRequirementMetError,
							requirementMet.buyerLegalEntitySubUnitNo,
						)
					}
					component={FormDropDownList}
				/>
				<Field
					id={"Kjøper-ID"}
					name={"Kjøper-ID"}
					label={t("FdirBuyerId") + "*"}
					textField={"displayName"}
					data={buyersFd}
					dataItemKey={"code"}
					defaultItem={{
						displayName: "Choose",
						code: null,
					}}
					currentValue={{
						code: buyersFd.find(
							(fd) => fd.code === form.buyerFdirId?.toString(),
						)?.code,
						displayName: buyersFd.find(
							(fd) => fd.code === form.buyerFdirId?.toString(),
						)?.displayName,
					}}
					onSelect={(data: DropDownItem) =>
						saveForm({ ...form, buyerFdirId: data.code as any })
					}
					disabled={!form.buyerLegalEntitySubUnitNo}
					loadDataOnClicked={() => fdNumber()}
					showErrorMessage={
						!handleIsValid(showRequirementMetError, requirementMet.buyerFdirId)
					}
					component={FormDropDownList}
				/>
				<Field
					id={"Godkjenningsnummer"}
					name={"Godkjenningsnummer"}
					label={t("ApprovalNumber")}
					data={foodSafetyApproveNumber}
					textField={"name"}
					dataItemKey={"code"}
					loadDataOnClicked={getFoodSafetyApprovalNumber}
					currentValue={{
						code: form.productionFacilityId,
						name: form.productionFacilityId,
					}}
					onSelect={(data: DropDownItem | null) => {
						saveForm({ ...form, productionFacilityId: data?.code ?? null });
					}}
					component={ComboBoxItem}
				/>
				<Field
					id={"Anvendelse"}
					name={"Anvendelse"}
					label={t("Usage") + "*"}
					filterable={true}
					textField={"displayName"}
					dataItemKey={"code"}
					// loadDataOnClicked={getSearchUsage}
					filterApi={getSearchUsageApi}
					defaultItem={{
						displayName: "Choose",
						code: null,
					}}
					currentValue={{ code: form.usageCode, displayName: form.usage }}
					// className="error"
					onSelect={(data: DropDownItem) =>
						saveForm({ ...form, usageCode: data.code, usage: data.displayName })
					}
					showErrorMessage={
						!handleIsValid(showRequirementMetError, requirementMet.usageCode)
					}
					component={FormDropDownListWithSearch}
				/>
				{showVesselQuota && (
					<Field
						id={"quotaVessel*"}
						name={"quotaVessel"}
						label={t("QuotaVessel")}
						value={t("Choose") + " " + t("QuotaVessel")}
						textField={"quotaVesselName"}
						dataItemKey={"id"}
						// disabled={
						// 	landingNoteRefExist
						// 		? landingNoteRefExist
						// 		: form?.lastCatchDate
						// 		? false
						// 		: true
						// }
						loadDataOnClicked={getQuotaVessel}
						data={quotaVessel}
						defaultItem={{
							quotaVesselName: t("Choose") + " " + t("QuotaVessel"),
							id: null,
						}}
						currentValue={{
							id: form.quotaVesselHullId,
							quotaVesselName: form.quotaVesselName,
						}}
						showErrorMessage={
							!handleIsValid(
								showRequirementMetError,
								requirementMet.quotaVesselHullId,
							)
						}
						onSelect={(quotaVessel: ExemptionVessel) => {
							saveForm({
								...form,
								quotaVesselHullId: quotaVessel.quotaVesselHullId as any,
								quotaVesselName: quotaVessel.quotaVesselName,
								quotaVesselRadioCallSignal:
									quotaVessel.quotaVesselRadioCallSignal,
								quotaVesselTypeId: quotaVessel.quotaVesselTypeId as any,
								quotaVesselRegistrationId:
									quotaVessel.quotaVesselRegistrationId as any,
							});
						}}
						itemRender={QuotaVesselRenderItem}
						component={FormDropDownList}
					/>
				)}
			</div>
		</>
	);
};

export default FormStepOne;

const ComboBoxItem: FC<FieldRenderProps> = ({
	setValueS,
	currentValue,
	onChange,
	...props
}) => {
	type selectionValue = {
		code: string | null;
		name: string | null;
	}

	//const [value, setValue] = useState({
	//	code: currentValue.code,
	//	name: currentValue.name,
	//});
	const [value, setValue] = useState<selectionValue>({
		code: currentValue?.code ?? null,
		name: currentValue?.name ?? null,
	});



	useEffect(() => {
		if (currentValue) {
		//	setValue({
		//		code: currentValue.code || "",
		//		name: currentValue.name || "",
		//	});
			setValue({
				code: currentValue?.code ?? null,
				name: currentValue?.name ?? null,
			});
		}
	}, [currentValue]);
	const handleOnChange = (event: any) => {
		const ev = event.nativeEvent;

		// Handle clearing the field
		if (event.value === null || event.value === "") {
			setValue({ code: null, name: null });
			props.onSelect({ code: null, name: null });
			return;
		}

		if (!ev || ev.code === "Enter" || ev.type === "focusout") {
			props.onSelect(value);
			return;
		}
		props.onSelect(event.value);
	};
	const focusedItemIndex = (data: any[], inputText: any, textField: any) => {
		if (inputText !== value.code || inputText !== value.name) {
			setValue({ code: inputText, name: inputText });
		}
		let text = inputText.toLowerCase();
		return data.findIndex((item: any) =>
			String(textField ? item[textField] : item)
				.toLowerCase()
				.includes(text),
		);
	};

	<ComboBox focusedItemIndex={focusedItemIndex} />;
	return (
		<FieldWrapper style={props.wrapperStyle}>
			<ItemLabel id={props.id} text={props.label} />

			<div>
				<ComboBox
					data={props.data}
					value={value}
					onChange={handleOnChange}
					onBlur={handleOnChange}
					allowCustom={true}
					placeholder={t("Select")}
					onOpen={props.loadDataOnClicked}
					textField={props.textField}
					dataItemKey={props.dataItemKey}
					focusedItemIndex={focusedItemIndex}
					// {...props}
				/>
			</div>
		</FieldWrapper>
	);
};

const handleIsValid = (showError: boolean, requirement: boolean) => {
	if (!showError) return !showError;
	return requirement;
};
